/**
 * Heading Component
 *
 * Reusable heading component that follows design system typography tokens
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering headings with consistent styling
 * - Open/Closed: Open for extension via props, closed for modification
 */

import React from 'react';
import {
  typographyColors,
  typography,
  fontPrimary,
} from '@/design-system/utils';

export interface HeadingProps {
  /**
   * The heading level (h1-h6)
   * @default 'h4'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * The heading text content
   */
  children: React.ReactNode;
  /**
   * Custom font size override
   */
  fontSize?: string;
  /**
   * Custom font weight override
   */
  fontWeight?: number;
  /**
   * Custom color override
   */
  color?: string;
  /**
   * Custom line height override
   */
  lineHeight?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

/**
 * Heading Component
 * Renders a heading with design system typography styles
 */
export const Heading: React.FC<HeadingProps> = ({
  as: Component = 'h4',
  children,
  fontSize,
  fontWeight,
  color,
  lineHeight,
  className = '',
  style = {},
}) => {
  // Default styles based on heading level
  const getDefaultStyles = (level: string) => {
    switch (level) {
      case 'h1':
        return {
          fontSize: typography.size['5xl'],
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight['52'],
        };
      case 'h2':
        return {
          fontSize: typography.size['4xl'],
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight.normal,
        };
      case 'h3':
        return {
          fontSize: typography.size['2xl'],
          fontWeight: typography.weight.bold,
          lineHeight: typography.lineHeight.normal,
        };
      case 'h4':
        return {
          fontSize: typography.size.lg,
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight.normal,
        };
      case 'h5':
        return {
          fontSize: typography.size.base,
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight.normal,
        };
      case 'h6':
        return {
          fontSize: typography.size.sm,
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight.normal,
        };
      default:
        return {
          fontSize: typography.size.lg,
          fontWeight: typography.weight.semibold,
          lineHeight: typography.lineHeight.normal,
        };
    }
  };

  const defaultStyles = getDefaultStyles(Component);

  return (
    <Component
      style={{
        fontFamily: fontPrimary,
        fontSize: fontSize || defaultStyles.fontSize,
        fontWeight: fontWeight || defaultStyles.fontWeight,
        color: color || typographyColors.primary,
        lineHeight: lineHeight || defaultStyles.lineHeight,
        margin: 0,
        padding: 0,
        ...style,
      }}
      className={className}
    >
      {children}
    </Component>
  );
};
